import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Shield as ShieldIcon,
  Send as SendIcon,
  CancelOutlined as CancelIcon,
} from '@mui/icons-material';
import { adminRolesApi } from '@/api/endpoints/adminRoles';
import { settingsApi as settingsApiImport } from '@/api/endpoints/settings';
import { InputAdornment } from '@mui/material';
import StatusDot from '@/components/common/StatusDot';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import PhoneInput from '@/components/common/PhoneInput';
import { settingsApi } from '@/api/endpoints/settings';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import ExportMenu, { ExportColumn } from '@/components/common/ExportMenu';
import { resolveUploadUrl } from '@/utils/format';
import PageHelpIcon from '@/components/PageHelpIcon';
import RecentChangesTimeline from '@/components/RecentChangesTimeline';
import { RoleSelector } from '@/components/settings/RoleSelector';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/api/endpoints/auth';
import { cleanGridSx, tableCardSx } from '@/theme/settingsTableStyles';
// ── UCR permission viewer dialog ──────────────────────────────────────

function UcrPermissionsDialog({ ucrId, onClose }: { ucrId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['ucr-permissions', ucrId],
    queryFn: async () => (await adminRolesApi.ucrPermissions(ucrId)).data,
    enabled: !!ucrId,
  });

  const { data: allPermissions = [] } = useQuery({
    queryKey: ['settings-permissions'],
    queryFn: async () => (await settingsApiImport.listPermissions()).data,
  });

  const permsByModule = useMemo(() => {
    const codeSet = new Set(data?.permission_codes ?? []);
    const grouped: Record<string, Array<{ code: string; description?: string; granted: boolean }>> = {};
    (allPermissions as Array<{ code: string; description?: string }>).forEach((p) => {
      const module = p.code.split('.')[0] ?? 'other';
      if (!grouped[module]) grouped[module] = [];
      grouped[module].push({ ...p, granted: codeSet.has(p.code) });
    });
    return grouped;
  }, [data, allPermissions]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Effective permissions
          </Typography>
          {data && (
            <Typography variant="caption" color="text.secondary">
              {data.user_full_name} · {data.role_name}
              {data.is_group_level ? ' · Group-level' : ''}
              {' · '}{data.permission_count} permissions
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && <Alert severity="error">Failed to load permissions.</Alert>}
        {data && (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Read-only view — resolved from role assignment. Edit via Settings → Roles.
            </Alert>
            {Object.keys(permsByModule)
              .sort()
              .map((module) => {
                const granted = permsByModule[module].filter((p) => p.granted);
                if (granted.length === 0) return null;
                return (
                  <Box key={module}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {module}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({granted.length})
                      </Typography>
                    </Typography>
                    <Divider sx={{ mb: 0.75 }} />
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {granted.map((p) => (
                        <Tooltip key={p.code} title={p.description ?? ''}>
                          <Chip
                            label={p.code.split('.').slice(1).join('.')}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>
                );
              })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Invitations tab — acceptance status of people you've invited ───────

const INVITE_STATUS: Record<string, { label: string; color: 'warning' | 'success' | 'default' | 'error' }> = {
  pending: { label: 'Pending', color: 'warning' },
  accepted: { label: 'Accepted', color: 'success' },
  expired: { label: 'Expired', color: 'default' },
  revoked: { label: 'Revoked', color: 'error' },
};

function InvitationsTab({ roles, canWrite }: { roles: any[]; canWrite: boolean }) {
  const { showSuccess, showError } = useNotificationStore();
  const queryClient = useQueryClient();

  const { data: invites = [], isLoading, error } = useQuery({
    queryKey: ['settings/invitations'],
    queryFn: () => settingsApi.listInvitations().then((r) => r.data),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => settingsApi.revokeInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/invitations'] });
      showSuccess('Invitation revoked.');
    },
    onError: () => showError('Could not revoke the invitation.'),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => settingsApi.resendInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/invitations'] });
      showSuccess('Invitation resent — a fresh email is on its way.');
    },
    onError: () => showError('Could not resend the invitation.'),
  });

  const roleName = (id: string) => roles.find((r: any) => r.id === id)?.name || '—';
  const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString() : '—');

  const columns: GridColDef[] = [
    {
      field: 'invitee', headerName: 'Invitee', flex: 1, minWidth: 180,
      valueGetter: (_v, row: any) => row.email,
      renderCell: (p) => (
        <Stack sx={{ height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2" noWrap>{p.row.email}</Typography>
          {(p.row.first_name || p.row.last_name) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {`${p.row.first_name || ''} ${p.row.last_name || ''}`.trim()}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: 'role_id', headerName: 'Role', flex: 0.6, minWidth: 110,
      renderCell: (p) => <Chip label={roleName(p.row.role_id)} size="small" variant="outlined" />,
    },
    {
      field: 'status', headerName: 'Status', flex: 0.5, minWidth: 110,
      renderCell: (p) => {
        const s = INVITE_STATUS[p.value as string] || { label: p.value, color: 'default' as const };
        return <Chip label={s.label} size="small" color={s.color} />;
      },
    },
    { field: 'created_at', headerName: 'Sent', flex: 0.5, minWidth: 100,
      valueFormatter: (v: string) => fmtDate(v) },
    { field: 'expires_at', headerName: 'Expires', flex: 0.5, minWidth: 100,
      valueFormatter: (v: string) => fmtDate(v) },
    {
      field: 'actions', headerName: '', width: 90, maxWidth: 90, sortable: false, filterable: false,
      renderCell: (p) => {
        if (!canWrite || p.row.status !== 'pending') return null;
        return (
          <Stack direction="row">
            <Tooltip title="Resend invitation email">
              <span>
                <IconButton size="small" onClick={() => resendMutation.mutate(p.row.id)} disabled={resendMutation.isPending}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Revoke invitation">
              <span>
                <IconButton size="small" color="error" onClick={() => revokeMutation.mutate(p.row.id)} disabled={revokeMutation.isPending}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Failed to load invitations. Please refresh the page.</Alert>;
  }

  return (
    <Box sx={tableCardSx}>
      <DataGrid
        autoHeight
        density="compact"
        rows={invites as any[]}
        columns={columns}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] },
        }}
        sx={cleanGridSx}
        localeText={{ noRowsLabel: 'No invitations yet. Use "Add User" to invite someone.' }}
      />
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────

export default function UsersPage({ embedded }: { embedded?: boolean }) {
  const { hasPermission: checkPerm, hasAnyPermission } = usePermission();

  const canRead = hasAnyPermission(['settings.users.read', 'settings.users.write']);
  const canWrite = checkPerm('settings.users.write');

  // Company context. The Users list + invite are scoped to the active company.
  // A session with no active company (e.g. a platform-admin account that
  // belongs on the Console, or a refreshed session that dropped its company
  // claim) can't load company-scoped data — we surface that clearly instead
  // of a raw "Failed to load" error.
  const companies = useAuthStore((s) => s.companies);
  const activeCompanyId = useAuthStore((s) => s.activeCompanyId);
  const setActiveCompany = useAuthStore((s) => s.setActiveCompany);
  const updateCompanyPermissions = useAuthStore((s) => s.updateCompanyPermissions);
  const updateCompanyModules = useAuthStore((s) => s.updateCompanyModules);
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);

  // Companies this admin may invite users INTO — those where they hold
  // settings.users.write. Drives the optional company selector on the invite
  // form so a group/multi-company admin can target the right company.
  const manageableCompanies = useMemo(
    () => companies.filter((c) => (c.permissions ?? []).includes('settings.users.write')),
    [companies],
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [viewPermsUcrId, setViewPermsUcrId] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [inviteCompanyId, setInviteCompanyId] = useState<string>('');
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    phone: '',
    role_id: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [reconnecting, setReconnecting] = useState(false);
  const [tab, setTab] = useState(0); // 0 = Users, 1 = Invitations
  const { showSuccess } = useNotificationStore();
  const queryClient = useQueryClient();

  const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
    // Key on the active company so switching companies refetches the list
    // instead of showing the previous company's users.
    queryKey: ['settings/users', activeCompanyId],
    queryFn: () => settingsApi.listUsers().then(res => res.data),
    enabled: !!activeCompanyId,
  });

  const { data: roles, error: rolesError } = useQuery({
    queryKey: ['settings/roles', activeCompanyId],
    queryFn: () => settingsApi.listRoles().then(res => res.data),
    enabled: !!activeCompanyId,
  });

  // Invite flow: admin sends email + role, user sets their own password via the
  // accept link. Admin never handles the initial password — eliminates the
  // "shared initial password" anti-pattern.
  const inviteMutation = useMutation({
    mutationFn: (data: { email: string; role_name: string; first_name?: string; last_name?: string; company_id?: string }) =>
      settingsApi.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/users'] });
      queryClient.invalidateQueries({ queryKey: ['settings/invitations'] });
      handleCloseDialog();
      showSuccess('Invitation sent! The user will receive an email to set their password.');
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('. '));
      } else {
        setError('Failed to send invitation. Please check the email address and try again.');
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => settingsApi.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/users'] });
      handleCloseDialog();
      showSuccess('User created successfully!');
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('. '));
      } else {
        setError('Failed to create user. Please check all fields and try again.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => settingsApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/users'] });
      handleCloseDialog();
      showSuccess('User updated successfully!');
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('. '));
      } else {
        setError('Failed to update user. Please check your input and try again.');
      }
    },
  });

  // No active company → company-scoped data can't load. Show an actionable
  // state instead of letting the queries fire and render red "Failed to load"
  // errors. Users who belong to a company can pick one here; platform-only
  // accounts are pointed to the Console.
  if (!activeCompanyId) {
    return (
      <Box sx={{ p: embedded ? 0 : 3 }}>
        {companies.length === 0 ? (
          <Alert severity="info">
            You&apos;re not assigned to a company yet, so there are no users to manage.
            If you&apos;re a platform administrator, manage tenants from the Console.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <Alert severity="info">Select a company to manage its users.</Alert>
            <TextField
              select
              size="small"
              label="Company"
              value=""
              onChange={(e) => void connectToCompany(e.target.value)}
              disabled={reconnecting}
            >
              {companies.map((c) => (
                <MenuItem key={c.company_id} value={c.company_id}>
                  {c.company_name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}
      </Box>
    );
  }

  if (!canRead) {
    return (
      <Box sx={{ p: embedded ? 0 : 3 }}>
        <Alert severity="error">
          You don&apos;t have permission to access this page. You need the &quot;Manage Users&quot; permission.
        </Alert>
      </Box>
    );
  }

  const handleEdit = (row: any) => {
    setEditingUser(row);
    setFormData({
      email: row.email || '',
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      password: '',
      phone: row.phone || '',
      role_id: row.role_id || '',
    });
    setOpenDialog(true);
  };

  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.first_name.trim()) errs.first_name = 'First name is required.';
    if (!formData.last_name.trim()) errs.last_name = 'Last name is required.';
    if (!formData.email.trim()) errs.email = 'Email address is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email address.';
    if (!formData.role_id) errs.role_id = 'Please select a role.';
    // New users: invite flow — no password required. Invitees set their own
    // password when they accept the link. Edit mode still allows password change.
    if (editingUser && formData.password) {
      if (formData.password.length < 8) errs.password = 'At least 8 characters.';
      else if (!/[A-Z]/.test(formData.password)) errs.password = 'Must include an uppercase letter.';
      else if (!/[0-9]/.test(formData.password)) errs.password = 'Must include a number.';
      else if (!/[^A-Za-z0-9]/.test(formData.password)) errs.password = 'Must include a special character.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    if (editingUser) {
      const payload: any = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone?.trim() || undefined,
        role_id: formData.role_id,
      };
      if (formData.password) payload.password = formData.password;
      updateMutation.mutate({ id: editingUser.id, data: payload });
    } else {
      // New user path: send an invite email. The user sets their own password.
      // role_name is resolved from the roles list using the selected role_id.
      const selectedRole = (roles ?? []).find((r: any) => r.id === formData.role_id);
      if (!selectedRole) {
        // This shouldn't happen (role_id is validated), but guard defensively:
        // an undefined role name would silently 404 on the backend.
        setFieldErrors({ role_id: 'Role not found. Please reload the page and try again.' });
        return;
      }
      inviteMutation.mutate({
        email: formData.email.trim(),
        role_name: selectedRole.name,
        first_name: formData.first_name.trim() || undefined,
        last_name: formData.last_name.trim() || undefined,
        // Target company — only sent when it differs from the active company
        // (server defaults to the active company otherwise).
        company_id:
          inviteCompanyId && inviteCompanyId !== activeCompanyId ? inviteCompanyId : undefined,
      });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({ email: '', first_name: '', last_name: '', password: '', phone: '', role_id: '' });
    setError('');
    setFieldErrors({});
  };

  const handleAddUser = () => {
    // Default the invite target to the company you're currently viewing.
    setInviteCompanyId(activeCompanyId ?? '');
    setOpenDialog(true);
  };

  // A 403 caused by a missing/stale company claim on the JWT (e.g. after a
  // token refresh that dropped the company, or a platform session). We KNOW
  // which company should be active from the store, so re-assert it via
  // switch-company — that re-mints the JWT with the company and recovers the
  // page without a full sign-out.
  const isNoCompanyError = (err: unknown): boolean => {
    const e = err as { response?: { status?: number; data?: { detail?: unknown } } };
    if (e?.response?.status !== 403) return false;
    const detail = e.response?.data?.detail;
    const text = typeof detail === 'string' ? detail.toLowerCase() : '';
    return text.includes('company context') || text.includes('company not found');
  };

  const connectToCompany = async (companyId: string) => {
    setReconnecting(true);
    try {
      const res = await authApi.switchCompany(companyId);
      // Refresh the cached permissions/modules for the company so the rest of
      // the app reflects the re-minted session immediately.
      if (res.data?.permissions) updateCompanyPermissions(companyId, res.data.permissions);
      if (res.data?.enabled_modules) updateCompanyModules(companyId, res.data.enabled_modules);
      setActiveCompany(companyId);
      await queryClient.invalidateQueries({ queryKey: ['settings/users'] });
      await queryClient.invalidateQueries({ queryKey: ['settings/roles'] });
    } finally {
      setReconnecting(false);
    }
  };

  const handleReconnect = () => {
    if (activeCompanyId) void connectToCompany(activeCompanyId);
  };

  const getRoleName = (roleId: string) => {
    const role = roles?.find((r: any) => r.id === roleId);
    return role?.name || '—';
  };

  const exportColumns: ExportColumn[] = [
    { field: 'full_name', headerName: 'Name', valueGetter: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() },
    { field: 'email', headerName: 'Email' },
    { field: 'phone', headerName: 'Phone' },
    { field: 'role_name', headerName: 'Role', valueGetter: (row) => String(row.role_name || getRoleName(String(row.role_id || ''))) },
    { field: 'is_active', headerName: 'Status', valueGetter: (row) => row.is_active ? 'Yes' : 'No' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'full_name', headerName: 'Name', flex: 1, minWidth: 140,
      valueGetter: (_v: unknown, row: any) => `${row.first_name} ${row.last_name}`,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <Avatar
            src={resolveUploadUrl(params.row.profile_photo)}
            sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'primary.main' }}
          >
            {`${params.row.first_name?.[0] || ''}${params.row.last_name?.[0] || ''}`.toUpperCase()}
          </Avatar>
          <Typography variant="body2" noWrap>{params.row.first_name} {params.row.last_name}</Typography>
        </Stack>
      ),
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 160 },
    { field: 'phone', headerName: 'Phone', flex: 0.6, minWidth: 100,
      valueFormatter: (value: string | null) => value || '—',
    },
    {
      field: 'role_name', headerName: 'Role', flex: 0.7, minWidth: 110,
      renderCell: (params) => (
        <Chip label={params.value || getRoleName(params.row.role_id)} size="small" variant="outlined" />
      ),
    },
    {
      field: 'is_active', headerName: 'Status', flex: 0.4, minWidth: 70,
      renderCell: (params) => <StatusDot active={params.value} />,
    },
    {
      field: 'actions', headerName: '', width: 90, maxWidth: 90, sortable: false, filterable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <Tooltip title="View effective permissions">
            <IconButton
              size="small"
              onClick={() => setViewPermsUcrId(params.row.ucr_id ?? null)}
              disabled={!params.row.ucr_id}
            >
              <ShieldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canWrite && (
            <Tooltip title="Edit user">
              <IconButton size="small" onClick={() => handleEdit(params.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  if (usersLoading && tab === 0) {
    return (
      <Box sx={{ p: embedded ? 0 : 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2, minHeight: 40 }}>
        <Tab label="Users" sx={{ minHeight: 40, textTransform: 'none' }} />
        <Tab label="Invitations" sx={{ minHeight: 40, textTransform: 'none' }} />
      </Tabs>

      {tab === 1 ? (
        <InvitationsTab roles={roles ?? []} canWrite={canWrite} />
      ) : (
      <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <PageHelpIcon pageKey="settings.users" />
          <TextField
            size="small" placeholder="Search by name or email..."
            value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            sx={{ width: 260 }}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <ExportMenu
            columns={exportColumns}
            rows={(users || [])}
            filename="users"
            title="Users"
          />
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser}>
              Add User
            </Button>
          )}
        </Stack>
      </Stack>

      {isNoCompanyError(usersError) || isNoCompanyError(rolesError) ? (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleReconnect} disabled={reconnecting}>
              {reconnecting
                ? 'Reconnecting…'
                : `Reconnect${activeCompany ? ` to ${activeCompany.company_name}` : ''}`}
            </Button>
          }
        >
          Your session lost its company context. Reconnect to continue — no need to sign in again.
        </Alert>
      ) : (
        <>
          {usersError && (
            <Alert severity="error" sx={{ mb: 2 }}>Failed to load users. Please refresh the page.</Alert>
          )}
          {rolesError && (
            <Alert severity="error" sx={{ mb: 2 }}>Failed to load roles. Please refresh the page.</Alert>
          )}
        </>
      )}

      <Box sx={tableCardSx}>
          <DataGrid
            autoHeight
            density="compact"
            rows={(users || []).filter((u: any) => {
              if (!searchFilter) return true;
              const q = searchFilter.toLowerCase();
              return (
                `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q)
              );
            })}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: 'full_name', sort: 'asc' }] },
            }}
            sx={cleanGridSx}
            localeText={{ noRowsLabel: 'No users found. Click "Add User" to create one.' }}
          />
      </Box>
      </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>
          {editingUser ? 'Edit User' : 'Invite User'}
        </BrandedDialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {!editingUser && (
            <Alert severity="info" sx={{ mb: 2 }}>
              An invitation email will be sent. The user sets their own password when they accept.
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <form>
            {/* Company selector — only when inviting (not editing) and the
                admin can manage users in more than one company. Lets a group/
                multi-company admin choose which company to add the user to. */}
            {!editingUser && manageableCompanies.length > 1 && (
              <TextField
                select
                fullWidth
                label="Company"
                value={inviteCompanyId}
                onChange={(e) => setInviteCompanyId(e.target.value)}
                margin="normal"
                helperText="The user will be added to this company."
              >
                {manageableCompanies.map((c) => (
                  <MenuItem key={c.company_id} value={c.company_id}>
                    {c.company_name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              fullWidth label="Email" type="email"
              value={formData.email}
              onChange={(e) => { setFormData({ ...formData, email: e.target.value }); setFieldErrors((p) => ({ ...p, email: '' })); }}
              margin="normal" required
              disabled={!!editingUser}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />
            <TextField
              fullWidth label="First Name"
              value={formData.first_name}
              onChange={(e) => { setFormData({ ...formData, first_name: e.target.value }); setFieldErrors((p) => ({ ...p, first_name: '' })); }}
              margin="normal" required
              error={!!fieldErrors.first_name}
              helperText={fieldErrors.first_name}
            />
            <TextField
              fullWidth label="Last Name"
              value={formData.last_name}
              onChange={(e) => { setFormData({ ...formData, last_name: e.target.value }); setFieldErrors((p) => ({ ...p, last_name: '' })); }}
              margin="normal" required
              error={!!fieldErrors.last_name}
              helperText={fieldErrors.last_name}
            />
            {/* Password field only shown when editing — new users set their own via invite link */}
            {editingUser && (
              <TextField
                fullWidth label="New Password (leave blank to keep)"
                type="password"
                value={formData.password}
                onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setFieldErrors((p) => ({ ...p, password: '' })); }}
                margin="normal"
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || 'Minimum 8 characters with uppercase, number, and special character'}
              />
            )}
            <PhoneInput
              label="Phone (optional)"
              value={formData.phone}
              onChange={(v) => setFormData({ ...formData, phone: v })}
              sx={{ mt: 2, mb: 1 }}
            />
            <RoleSelector
              value={formData.role_id}
              onChange={(roleId) => setFormData({ ...formData, role_id: roleId })}
              roles={roles ?? []}
              required
              error={!!fieldErrors.role_id}
              helperText={fieldErrors.role_id}
              sx={{ mt: 2, mb: 1 }}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={inviteMutation.isPending || createMutation.isPending || updateMutation.isPending}
          >
            {(inviteMutation.isPending || updateMutation.isPending)
              ? (editingUser ? 'Saving...' : 'Sending Invite...')
              : (editingUser ? 'Save Changes' : 'Send Invite')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── UCR Permission Viewer ── */}
      {viewPermsUcrId && (
        <UcrPermissionsDialog
          ucrId={viewPermsUcrId}
          onClose={() => setViewPermsUcrId(null)}
        />
      )}

      {/* ── Recent Changes ── */}
      <RecentChangesTimeline tableName="users" limit={5} />
    </Box>
  );
}
